import { PixelBuffer } from "./PixelBuffer";

// Pure pixel-level helper for applying an affine transform to a PixelBuffer.
// The matrix is column-major in the algebraic sense but stored flat:
//
//     [ a c e ]    x' = a*x + c*y + e
//     [ b d f ]    y' = b*x + d*y + f
//     [ 0 0 1 ]
//
// Sampling uses an inverse-map: for each destination pixel we map back to
// source coordinates and sample. Bilinear is the default — it dramatically
// reduces the staircase artefacts of nearest-neighbour for rotation/scale.
//
// Premultiplied alpha is used while sampling to avoid the classic "halo"
// you get from interpolating straight colour against fully-transparent
// pixels (whose colour channels are nominally undefined).

export interface Affine2D {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly d: number;
  readonly e: number;
  readonly f: number;
}

export function identity(): Affine2D {
  return { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
}

// m1 ∘ m2 — applies m2 first, then m1. Equivalent to multiplying the
// 3×3 matrices in standard order.
export function compose(m1: Affine2D, m2: Affine2D): Affine2D {
  return {
    a: m1.a * m2.a + m1.c * m2.b,
    b: m1.b * m2.a + m1.d * m2.b,
    c: m1.a * m2.c + m1.c * m2.d,
    d: m1.b * m2.c + m1.d * m2.d,
    e: m1.a * m2.e + m1.c * m2.f + m1.e,
    f: m1.b * m2.e + m1.d * m2.f + m1.f,
  };
}

export function inverse(m: Affine2D): Affine2D {
  const det = m.a * m.d - m.b * m.c;
  if (det === 0) {
    // Degenerate (e.g. scale by 0). Return identity rather than NaNs — callers
    // should guard against this, but we don't want to crash a renderer.
    return identity();
  }
  const inv = 1 / det;
  return {
    a: m.d * inv,
    b: -m.b * inv,
    c: -m.c * inv,
    d: m.a * inv,
    e: (m.c * m.f - m.d * m.e) * inv,
    f: (m.b * m.e - m.a * m.f) * inv,
  };
}

export function translate(tx: number, ty: number): Affine2D {
  return { a: 1, b: 0, c: 0, d: 1, e: tx, f: ty };
}

export function scale(sx: number, sy: number): Affine2D {
  return { a: sx, b: 0, c: 0, d: sy, e: 0, f: 0 };
}

export function rotate(radians: number): Affine2D {
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 };
}

export interface AffineTransformOptions {
  readonly outputWidth: number;
  readonly outputHeight: number;
  readonly sampling?: "nearest" | "bilinear";
}

// Warp `src` through `matrix` (source → output coordinates) into a fresh
// PixelBuffer of size (outputWidth, outputHeight). Pixels outside the source
// rectangle become fully transparent.
export function transformBuffer(
  src: PixelBuffer,
  matrix: Affine2D,
  opts: AffineTransformOptions,
): PixelBuffer {
  const ow = opts.outputWidth | 0;
  const oh = opts.outputHeight | 0;
  if (ow <= 0 || oh <= 0) {
    return new PixelBuffer(Math.max(1, ow), Math.max(1, oh), src.mode);
  }
  const sampling = opts.sampling ?? "bilinear";
  const dst = new PixelBuffer(ow, oh, src.mode);

  // We inverse-map: for each destination pixel, find the corresponding
  // source pixel. The provided matrix maps source→dest; we invert it.
  const inv = inverse(matrix);

  const srcW = src.width;
  const srcH = src.height;
  const srcData = src.data;
  const dstData = dst.data;

  for (let y = 0; y < oh; y++) {
    // The destination pixel centre is (x + 0.5, y + 0.5) but we use the
    // simpler integer convention (x, y) consistently. The +0.5 offset would
    // only matter for sub-pixel alignment in a tiled compositor and would
    // need to match the renderer; keep it integer for now.
    let srcX = inv.a * 0 + inv.c * y + inv.e;
    let srcY = inv.b * 0 + inv.d * y + inv.f;
    const stepX = inv.a;
    const stepY = inv.b;
    for (let x = 0; x < ow; x++) {
      const dstIdx = (y * ow + x) * 4;
      if (sampling === "nearest") {
        sampleNearest(srcData, srcW, srcH, srcX, srcY, dstData, dstIdx);
      } else {
        sampleBilinear(srcData, srcW, srcH, srcX, srcY, dstData, dstIdx);
      }
      srcX += stepX;
      srcY += stepY;
    }
  }

  dst.markDirty();
  return dst;
}

function sampleNearest(
  src: Uint8ClampedArray,
  sw: number,
  sh: number,
  sx: number,
  sy: number,
  dst: Uint8ClampedArray,
  di: number,
): void {
  const ix = Math.floor(sx);
  const iy = Math.floor(sy);
  if (ix < 0 || iy < 0 || ix >= sw || iy >= sh) {
    dst[di] = 0;
    dst[di + 1] = 0;
    dst[di + 2] = 0;
    dst[di + 3] = 0;
    return;
  }
  const si = (iy * sw + ix) * 4;
  dst[di] = src[si] ?? 0;
  dst[di + 1] = src[si + 1] ?? 0;
  dst[di + 2] = src[si + 2] ?? 0;
  dst[di + 3] = src[si + 3] ?? 0;
}

// Bilinear with premultiplied-alpha blending so edges over transparent
// pixels don't bleed black. We sample the four neighbours, premultiply
// each by its alpha, weight, then un-premultiply at the end. Neighbours
// outside the source contribute zero (RGB and alpha) — clamp-to-zero.
function sampleBilinear(
  src: Uint8ClampedArray,
  sw: number,
  sh: number,
  sx: number,
  sy: number,
  dst: Uint8ClampedArray,
  di: number,
): void {
  // Even the four neighbours might be fully outside the source rect — in
  // which case we write transparent. Early reject when the sample point
  // is more than 1px outside the source on any side.
  if (sx <= -1 || sy <= -1 || sx >= sw || sy >= sh) {
    dst[di] = 0;
    dst[di + 1] = 0;
    dst[di + 2] = 0;
    dst[di + 3] = 0;
    return;
  }

  const x0 = Math.floor(sx);
  const y0 = Math.floor(sy);
  const x1 = x0 + 1;
  const y1 = y0 + 1;
  const fx = sx - x0;
  const fy = sy - y0;

  const w00 = (1 - fx) * (1 - fy);
  const w10 = fx * (1 - fy);
  const w01 = (1 - fx) * fy;
  const w11 = fx * fy;

  let r = 0,
    g = 0,
    b = 0,
    a = 0;

  if (x0 >= 0 && y0 >= 0 && x0 < sw && y0 < sh) {
    const i = (y0 * sw + x0) * 4;
    const sa = src[i + 3] ?? 0;
    r += ((src[i] ?? 0) * sa * w00) / 255;
    g += ((src[i + 1] ?? 0) * sa * w00) / 255;
    b += ((src[i + 2] ?? 0) * sa * w00) / 255;
    a += sa * w00;
  }
  if (x1 >= 0 && y0 >= 0 && x1 < sw && y0 < sh) {
    const i = (y0 * sw + x1) * 4;
    const sa = src[i + 3] ?? 0;
    r += ((src[i] ?? 0) * sa * w10) / 255;
    g += ((src[i + 1] ?? 0) * sa * w10) / 255;
    b += ((src[i + 2] ?? 0) * sa * w10) / 255;
    a += sa * w10;
  }
  if (x0 >= 0 && y1 >= 0 && x0 < sw && y1 < sh) {
    const i = (y1 * sw + x0) * 4;
    const sa = src[i + 3] ?? 0;
    r += ((src[i] ?? 0) * sa * w01) / 255;
    g += ((src[i + 1] ?? 0) * sa * w01) / 255;
    b += ((src[i + 2] ?? 0) * sa * w01) / 255;
    a += sa * w01;
  }
  if (x1 >= 0 && y1 >= 0 && x1 < sw && y1 < sh) {
    const i = (y1 * sw + x1) * 4;
    const sa = src[i + 3] ?? 0;
    r += ((src[i] ?? 0) * sa * w11) / 255;
    g += ((src[i + 1] ?? 0) * sa * w11) / 255;
    b += ((src[i + 2] ?? 0) * sa * w11) / 255;
    a += sa * w11;
  }

  if (a <= 0) {
    dst[di] = 0;
    dst[di + 1] = 0;
    dst[di + 2] = 0;
    dst[di + 3] = 0;
    return;
  }

  // Un-premultiply. `a` is in [0, 255]; r/g/b were accumulated as
  // (channel * alpha / 255) * weight, so dividing by (a / 255) yields the
  // straight-colour value.
  const invA = 255 / a;
  dst[di] = Math.round(r * invA);
  dst[di + 1] = Math.round(g * invA);
  dst[di + 2] = Math.round(b * invA);
  dst[di + 3] = Math.round(a);
}
